import { styled } from "goober";
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { PressButton } from "./PressButton";
import { sidePanelStyles } from "./sidePanelStyles";

export const sidePanelOverlayStyles = `
  &[data-overlay="true"] {
    ${sidePanelStyles}
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: min(480px, 100%);
    height: auto;
    padding: 0;
    overflow: hidden;
    z-index: 2;
    transform: translateX(0);
    visibility: visible;
    transition:
      transform var(--motion-base) var(--ease-out),
      visibility 0s;

    &[data-expanded="false"] {
      transform: translateX(100%);
      visibility: hidden;
      transition:
        transform var(--motion-base) var(--ease-out),
        visibility 0s linear var(--motion-base);
    }
  }
`;

export const SidePanelDismiss = styled(PressButton)`
  position: absolute;
  inset: 0;
  z-index: 1;
  cursor: default;
`;

export function useSidePanelOverlay(open: boolean, onClose: () => void) {
  const [workspaceWidth, setWorkspaceWidth] = useState<number | null>(null);
  const isOverlay = workspaceWidth !== null && workspaceWidth < 720;
  const workspaceRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const close = useEffectEvent(onClose);
  const observeWorkspace = (workspace: HTMLDivElement | null) => {
    workspaceRef.current = workspace;
    if (!workspace) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWorkspaceWidth(entry.contentRect.width);
    });
    observer.observe(workspace);
    return () => {
      observer.disconnect();
      workspaceRef.current = null;
    };
  };

  useEffect(() => {
    if (!open || !isOverlay) return;
    const panel = panelRef.current;
    const previousFocus = document.activeElement;
    // Let the opener’s native pointer focus settle before focusing the panel.
    const focusFrame = requestAnimationFrame(() => {
      if (!panel?.contains(document.activeElement))
        panel?.focus({ preventScroll: true });
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.stopPropagation();
      close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown);
      if (
        previousFocus instanceof HTMLElement &&
        previousFocus.isConnected &&
        (panel?.contains(document.activeElement) ||
          document.activeElement === document.body)
      ) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, [open, isOverlay]);

  return { isOverlay, workspaceRef, panelRef, observeWorkspace };
}
